import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import posthog from 'posthog-js';
import Sidebar from './Sidebar';
import CommandPalette from './CommandPalette';
import QuickActions from './QuickActions';
import OnboardingTour from './OnboardingTour';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [trial, setTrial] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => sessionStorage.getItem('trial_banner_dismissed') === 'true');
  const [paymentIssue, setPaymentIssue] = useState(false);
  const [paymentBannerDismissed, setPaymentBannerDismissed] = useState(() => sessionStorage.getItem('payment_banner_dismissed') === 'true');
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Fetch trial status
  useEffect(() => {
    api.get('/account')
      .then(res => {
        if (res.data.trial && res.data.trial.active) {
          setTrial(res.data.trial);
        }
        if (res.data.paymentIssue) {
          setPaymentIssue(true);
        }
      })
      .catch(() => {});
  }, []);

  const handleToggle = useCallback(() => {
    setCollapsed(c => !c);
  }, []);

  // Cmd+K to open search
  React.useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(s => {
          if (!s && !sessionStorage.getItem('cmd_k_used')) {
            posthog.capture('feature_discovered', { feature_name: 'command_palette' });
            sessionStorage.setItem('cmd_k_used', 'true');
          }
          return !s;
        });
      }
      if (e.key === 'Escape') {
        setShowSearch(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="app-layout">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Sidebar collapsed={collapsed} onToggle={handleToggle} />
      <main id="main-content" className={`main-content ${collapsed ? 'sidebar-collapsed' : ''}`}>
        {trial && trial.active && !bannerDismissed && (
          <div style={{
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            fontSize: '0.88rem',
            fontWeight: 500,
            background: trial.daysRemaining <= 3 ? '#fef3c7' : '#ede9fe',
            color: trial.daysRemaining <= 3 ? '#92400e' : '#5b21b6',
            borderBottom: `1px solid ${trial.daysRemaining <= 3 ? '#fde68a' : '#c4b5fd'}`,
          }}>
            <span>
              {typeof t.trialBanner === 'function'
                ? t.trialBanner({ days: trial.daysRemaining })
                : `Pro trial — ${trial.daysRemaining} day${trial.daysRemaining !== 1 ? 's' : ''} remaining`}
            </span>
            <button
              onClick={() => navigate('/dashboard/settings')}
              style={{
                background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '5px 14px',
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t.upgrade || 'Upgrade'}
            </button>
            <button
              onClick={() => { setBannerDismissed(true); sessionStorage.setItem('trial_banner_dismissed', 'true'); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'inherit', padding: '0 4px', lineHeight: 1 }}
              aria-label="Dismiss"
            >&times;</button>
          </div>
        )}
        {paymentIssue && !paymentBannerDismissed && (
          <div style={{
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            fontSize: '0.88rem',
            fontWeight: 500,
            background: '#fee2e2',
            color: '#dc2626',
            borderBottom: '1px solid #fecaca',
          }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '0.9rem' }}></i>
            <span>
              {t.paymentIssueBanner || 'Payment issue — your card was declined. Update your billing info to avoid service interruption.'}
            </span>
            <button
              onClick={async () => {
                try {
                  const res = await api.get('/stripe/portal');
                  if (res.data.url) window.location.href = res.data.url;
                } catch {
                  navigate('/dashboard/settings');
                }
              }}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '5px 14px',
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t.updateBilling || 'Update Billing'}
            </button>
            <button
              onClick={() => { setPaymentBannerDismissed(true); sessionStorage.setItem('payment_banner_dismissed', 'true'); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'inherit', padding: '0 4px', lineHeight: 1 }}
              aria-label="Dismiss"
            >&times;</button>
          </div>
        )}
        <Outlet />
        <footer style={{ padding: '20px 24px', borderTop: '1px solid var(--border-color, #e5e7eb)', marginTop: 40, display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link to="/terms" style={{ fontSize: '0.78rem', color: 'var(--text-muted, #9ca3af)', textDecoration: 'none' }}>Terms of Service</Link>
          <Link to="/privacy" style={{ fontSize: '0.78rem', color: 'var(--text-muted, #9ca3af)', textDecoration: 'none' }}>Privacy Policy</Link>
          <Link to="/cookies" style={{ fontSize: '0.78rem', color: 'var(--text-muted, #9ca3af)', textDecoration: 'none' }}>Cookie Policy</Link>
        </footer>
      </main>
      <QuickActions navigate={navigate} />
      {showSearch && <CommandPalette onClose={() => setShowSearch(false)} navigate={navigate} />}
      <OnboardingTour />
    </div>
  );
}
